import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import type { RootState } from '..';

import { clientApi } from '@/api';
import type { IThunkAPI } from '../types';
import type { IClient, IClientState, IClientSummary } from '@/store/admin/types/types';
import { isFulfilledAction, isPendingAction, isRejectedAction } from '@/helpers';

export const initialState: IClientState = {
  clientList: [],
  client: null,
  clientKeyValue: [],
  isClientError: false,
  isClientSuccess: false,
  isClientFetching: false,
};

export const getClientList = createAsyncThunk<IClientSummary[], void, IThunkAPI>(
  'get/client/list',
  async (_, thunkAPI) => {
    try {
      const { data } = await clientApi.getClientList();
      return data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getClientById = createAsyncThunk<IClient | null, number, IThunkAPI>(
  'get/client/clientId',
  async (id, thunkAPI) => {
    try {
      const { data } = await clientApi.getClientById(id);
      return data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const clientSlice = createSlice({
  name: 'client',
  initialState,
  reducers: {
    clearClients(state) {
      state.clientList = [];
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getClientList.fulfilled, (state, action) => {
        const sortedList = action.payload.sort((a: IClientSummary, b: IClientSummary) => {
          const fa = a.clientName.toLowerCase(),
            fb = b.clientName.toLowerCase();

          if (fa < fb) {
            return -1;
          }
          if (fa > fb) {
            return 1;
          }
          return 0;
        });
        state.clientList = sortedList;
        state.clientKeyValue = sortedList.reduce((a: any, x: any) => ({ ...a, [x.clientId]: x }), {});
      })
      .addCase(getClientById.fulfilled, (state, action) => {
        state.client = action.payload;
      })
      .addMatcher(isFulfilledAction, state => {
        state.isClientSuccess = true;
        state.isClientError = false;
        state.isClientFetching = false;
      })
      .addMatcher(isRejectedAction, state => {
        state.isClientError = true;
        state.isClientSuccess = false;
        state.isClientFetching = false;
      })
      .addMatcher(isPendingAction, state => {
        state.isClientFetching = true;
      });
  },
});

// actions
export const { clearClients } = clientSlice.actions;

// selectors
export const clientListAll = (state: RootState): IClientState['clientList'] => state.client.clientList;
export const client = (state: RootState): IClientState['client'] => state.client.client;

export default clientSlice.reducer;
